import React from 'react';
import { hideVisually } from 'polished';
import styled from 'styled-components/macro';

import { ThemeProps } from 'types/styled';
import generateRandomHash from 'helpers/generateRandomHash';

import { ChannelTypes, EventCategories } from 'redux/reducers/subscriptions';

import Checkbox from 'components/base/Checkbox/Checkbox';
import { CheckboxLabel } from 'components/base/CheckboxDropdown/styles';
import colors from 'styles/themes/colors';

type TCustomListCheckbox = { selected: boolean; value: string };
type TNotificationCheckbox = {
  selected: boolean;
  channelType: ChannelTypes;
  eventCategory: EventCategories;
};

interface IChannelSubscriptionProps {
  label?: string;
  checkingParameters: TCustomListCheckbox | TNotificationCheckbox;
  toggleCheckedHandler: Function;
  compact?: boolean;
  disabled?: boolean;
}

export const CheckboxContainer = styled.div<ThemeProps<'div'>>`
  color: ${(p: any): string => p.theme.NightBlue};
  &[disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
  & input {
    ${hideVisually};
  }
  svg {
    min-width: 20px;
  }
`;

/**
 * This component manages the subscription state for a given ChannelType and EventCategory.
 * When the the checkbox is selected this indicates that the EventCategory needs to be added to
 * the given ChannelType.
 *
 * This component is 'dumb', in the sense that it does not manage any state itself, it only shows
 * the given state and notifies the outside when its checkbox has been clicked.
 */
const LabeledCheckbox: React.FC<IChannelSubscriptionProps> = (props) => {
  const {
    label,
    checkingParameters,
    toggleCheckedHandler,
    compact = false,
    disabled,
  } = props;

  const id = generateRandomHash();

  const handleChange = (): void => {
    toggleCheckedHandler(checkingParameters);
  };

  return (
    <CheckboxContainer disabled={disabled}>
      <input
        id={id}
        type="checkbox"
        checked={checkingParameters.selected}
        onChange={handleChange}
      />
      <CheckboxLabel htmlFor={id} compact={compact}>
        <Checkbox
          enabledText="Disable"
          isChecked={checkingParameters.selected}
          disabledText="Enable"
          color={disabled ? colors.CasperGrey : colors.AccentGreen}
          insideDropdown
        />
        {label && label.length && <span>{label}</span>}
      </CheckboxLabel>
    </CheckboxContainer>
  );
};

export default LabeledCheckbox;
